import React, { useState, useCallback, useEffect, useContext, useRef, useMemo } from 'react'
import { useTranslation } from "react-i18next";
import styles from './ModuleRecords.module.css'
import { Tabs, Tab } from 'react-bootstrap';
import CrmGrid from '../../../UI/Crm/CrmGrid/CrmGrid';
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from 'react-redux'
import ModuleNewRecord from './ModuleNewRecord';
import ModuleFilterRecords from './ModuleFilterRecords';
import SubModal from '../../../UI/Crm/SubModal/SubModal';
import { getBaseRecordsByModuleId, deleteBaseRecord, deleteBaseRecords } from '../../../../axios/requests/baseRecordReq'
import ModuleRecordRelation from './ModuleRecordRelation';
import ConfirmationModal from '../../../UI/ConfirmationModal/ConfirmationModal';
import ModuleImportRecords from './ModuleImportRecords';
import AlertContext from '../../../../context/AlertModelContext/AlertModalContext';
import PasswordConfirmationModal from '../../../UI/PasswordConfirmationModal/PasswordConfirmationModal';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

import {
    clearModuleFilters, getModuleLastFilters
} from '../../../../axios/requests/moduleFilterReq';   // adjust relative path if needed

dayjs.extend(utc);
dayjs.extend(timezone);

const ModuleRecords = ({
    moduleObj,
    multiSelectionEnable,
    isGridMinimized,
    onMinimizeToggleClicked,
    gridTitle,
    isSubGrid,
    gridCssClass,
    relatedToRecordId,
    relatedModuleObj,
    suppressLinkModules,
    suppressOpenNewRecord,
    suppressOpenRecord,
    onSelectionChanged,
    onNewRowAdded,
    mode,
    onSelectConv,
    OnAddToDialListClicked,
    newRecord = false,
    useLocalFilters = false,
    showBulkEmailButton = true,
    showBulkDialListButton = true
}) => {
    const [showNewRecordModal, setShowNewRecordModal] = useState(false)
    const [showFilterRecordsModal, setFilterRecordsModal] = useState(false)
    const [linkRecordModal, setLinkRecordModal] = useState(false)
    const [unLinkRecordModal, setUnLinkRecordModal] = useState(false)
    const [importRecordsModal, setImportRecordsModal] = useState(false)
    const [reloadGridToggle, setReloadGridTogggle] = useState(false)
    const [filtersArr, setFiltersArr] = useState([])
    const [selectedRows, setSelectedRows] = useState([])
    const [confirmationModal, setConfirmationModal] = useState({ show: false })
    const [showPasswordConfirmImport, setShowPasswordConfirmImport] = useState(false)
    const [showPasswordConfirmExport, setShowPasswordConfirmExport] = useState(false)
    
    // Pagination state for server-side pagination
    const [currentPage, setCurrentPage] = useState(1)
    const [currentPageSize, setCurrentPageSize] = useState(10)
    const [totalRecords, setTotalRecords] = useState(0)
    const [pageSizeInitialized, setPageSizeInitialized] = useState(false)
    const [isInitialLoad, setIsInitialLoad] = useState(true)
    const [waitingForDynamicPageSize, setWaitingForDynamicPageSize] = useState(false)
    const dynamicPageSizeUpdateRef = useRef(false)
    const lastRequestRef = useRef('')
    const initialFetchDelayRef = useRef(null)

    // Sorting state for server-side sorting
    const [sortBy, setSortBy] = useState('createdAt')
    const [sortOrder, setSortOrder] = useState('desc')


    const [t] = useTranslation('crm')
    const alertCtx = useContext(AlertContext)
    const navigate = useNavigate();
    const crmState = useSelector(state => state.crm)
    const loggedUser = useSelector(state => state.login)
    let selectedCrmModule
    if (moduleObj) {
        selectedCrmModule = moduleObj
    }
    else {
        selectedCrmModule = crmState?.selectedCrmModule
    }
    const isDeletePermissions = crmState?.selectedCrmModule?.userPermissions?.enabledActions?.delete
    const isInsertPermissions = crmState?.selectedCrmModule?.userPermissions?.enabledActions?.insert
    const isUpdatePermissions = crmState?.selectedCrmModule?.userPermissions?.enabledActions?.update

    const moduleName = selectedCrmModule?.name
    const [columnDefs, setColumnDefs] = useState([]);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [linkLoading, setLinkLoading] = useState(false);
    const [newRecordLoading, setNewRecordLoading] = useState(false);


    useEffect(() => {
        setShowNewRecordModal(newRecord)
    }, [newRecord])

    // Initialize page size and set up dynamic page size waiting
    useEffect(() => {
        if (selectedCrmModule?._id && !pageSizeInitialized) {
            setPageSizeInitialized(true);
            setIsInitialLoad(true);
            dynamicPageSizeUpdateRef.current = false;
            setWaitingForDynamicPageSize(true);
        }
    }, [selectedCrmModule?._id, pageSizeInitialized])

    // Separate effect for the timeout to avoid cleanup issues
    useEffect(() => {
        if (selectedCrmModule?._id && pageSizeInitialized && waitingForDynamicPageSize) {
            const timeoutId = setTimeout(() => {
                setWaitingForDynamicPageSize(false);
            }, 500);

            return () => {
                clearTimeout(timeoutId);
            };
        }
    }, [selectedCrmModule?._id, pageSizeInitialized, waitingForDynamicPageSize])


    // Helper function to find label in nested dropdown structure
    const findLabelInDropdownItems = useCallback((dropdownItems, value) => {
        if (!dropdownItems || !value) return null;

        for (const item of dropdownItems) {
            // Check if this item matches the value
            if (item.value === value) {
                return item.key;
            }

            // Recursively check children
            if (item.children && item.children.length > 0) {
                const foundLabel = findLabelInDropdownItems(item.children, value);
                if (foundLabel) return foundLabel;
            }
        }

        return null;
    }, []);

    const getMergedStaticDynamicRowFields = useCallback((rows = []) => {
        const mergedRows = rows.map(record => {
            const dynamicFields = (record.fields || []).reduce((acc, cur) => {
                // Find the field definition in the module to check field type
                const fieldDef = selectedCrmModule?.sections
                    ?.flatMap(section => section.fields || [])
                    ?.find(field => field.name === cur.name);

                // Handle select fields - both cascading and regular
                if (fieldDef && fieldDef.type === 'select') {
                    if (typeof cur.value === 'object' && cur.value !== null && cur.value.parent) {
                        // Handle cascading dropdown objects - build hierarchy display with labels
                        const parts = [];
                        const dropdownItems = fieldDef.dropDownId?.dropDownItems;

                        // Add children levels first (in reverse order for proper hierarchy)
                        if (cur.value.children && cur.value.children.length > 0) {
                            const sortedChildren = cur.value.children.sort((a, b) => b.level - a.level);
                            for (const child of sortedChildren) {
                                const childLabel = findLabelInDropdownItems(dropdownItems, child.value);
                                parts.push(childLabel || child.value);
                            }
                        }

                        // Add parent level - lookup label for parent value
                        const parentLabel = findLabelInDropdownItems(dropdownItems, cur.value.parent);
                        parts.push(parentLabel || cur.value.parent);

                        acc[cur.name] = parts.filter(part => part).join('→');
                    } else {
                        // Handle regular select fields - lookup the label from dropdown options
                        const selectedOption = fieldDef.dropDownId?.dropDownItems?.find(item => item.value === cur.value);
                        acc[cur.name] = selectedOption?.key || cur.value; // Fallback to value if label not found
                    }
                } else {
                    acc[cur.name] = cur.value;
                }
                return acc;
            }, {});

            return {
                ...record,
                ...dynamicFields
            };
        });
        return mergedRows
    }, [selectedCrmModule?._id, findLabelInDropdownItems]) // Use stable reference instead of whole object

    useEffect(() => {
        const fetchLastFilters = async () => {
            if (!selectedCrmModule?._id) {
                setFiltersArr([]); // Initialize with empty array instead of null
                return;
            }

            // Skip backend fetch if using local filters only
            if (useLocalFilters) {
                setFiltersArr([]);
                return;
            }

            const lastFilters = await getModuleLastFilters(selectedCrmModule._id);

            if (lastFilters.length) {
                const savedValues = {};
                lastFilters.forEach(f => (savedValues[f.field] = f.value));
                setFiltersArr(lastFilters)
            }
            else {
                setFiltersArr([]);                  // mark as fetched but empty
            }
        };

        fetchLastFilters();
    }, [selectedCrmModule?._id, useLocalFilters]);  // eslint-disable-line

    // Memoize the dependency values to prevent unnecessary re-renders from parent
    const effectDeps = useMemo(() => {
        return {
            moduleId: selectedCrmModule?._id,
            currentPage,
            currentPageSize,
            sortBy,
            sortOrder,
            filtersArr: filtersArr ? JSON.stringify(filtersArr) : null,
            reloadGridToggle,
            mode,
            relatedToRecordId,
            isGridMinimized,
            pageSizeInitialized,
            waitingForDynamicPageSize
        };
    }, [selectedCrmModule?._id, currentPage, currentPageSize, sortBy, sortOrder, filtersArr, reloadGridToggle, mode, relatedToRecordId, isGridMinimized, pageSizeInitialized, waitingForDynamicPageSize]);

    useEffect(() => {
        if (!selectedCrmModule || !selectedCrmModule._id || isGridMinimized) {
            return;
        }
        
        // For server-side pagination, wait for page size to be initialized to prevent double requests
        if (!pageSizeInitialized) {
            return;
        }

        // Wait for dynamic page size calculation to complete before fetching
        if (waitingForDynamicPageSize) {
            return;
        }

        // Create a unique request key to prevent duplicate requests
        const requestKey = `${effectDeps.moduleId}-${effectDeps.currentPage}-${effectDeps.currentPageSize}-${effectDeps.sortBy}-${effectDeps.sortOrder}-${effectDeps.filtersArr}-${effectDeps.reloadGridToggle}-${effectDeps.mode || ''}-${effectDeps.relatedToRecordId || ''}`;

        // If this is the same request as the last one, skip it
        if (lastRequestRef.current === requestKey) {
            return;
        }

        lastRequestRef.current = requestKey;

        const fetchRecordsAndColumns = async () => {
            setLoading(true);
            const startTime = Date.now();

            try {
                // 1. Generate columnDefs
                const allFields = selectedCrmModule.sections
                    .filter(section => section.name !== 'systemSection')
                    .flatMap(section => (section.fields || []).filter(field => field.showColumns));

                const sortedFields = allFields.sort((a, b) => (a.order || 0) - (b.order || 0));

                // 2. Fetch rows first to calculate column widths
                const filters = [...filtersArr]

                if (relatedToRecordId && mode === 'unlink') {
                    filters.push(
                        {
                            field: "relatedRecords.records",
                            operator: "equals",
                            value: relatedToRecordId
                        }
                    )
                }

                // Use pagination for server-side or fallback to large number for client-side
                const pageToFetch = Math.max(1, currentPage) // Ensure page is never 0 or negative
                const limitToFetch = currentPageSize

                const fetchedData = await getBaseRecordsByModuleId(selectedCrmModule._id, pageToFetch, limitToFetch, sortBy, sortOrder, filters);

                // 3. Extract records and merge fields
                const fetchedRows = fetchedData?.data?.records || [];
                const mergedRows = getMergedStaticDynamicRowFields(fetchedRows)

                // 4. Calculate dynamic column widths based on content - PRIORITIZE HEADER VISIBILITY
                const calculateColumnWidth = (field, rows) => {
                    const headerText = t(field.label);
                    const headerLength = headerText.length;
                    let maxContentLength = headerLength;

                    // Check content in all rows for this field
                    rows.forEach(row => {
                        let cellValue = '';

                        if (row[field.name] !== undefined) {
                            if (field.type === 'select') {
                                // For select fields, use the processed value (which includes arrow formatting)
                                cellValue = String(row[field.name] || '');
                            } else if (['datetime', 'date'].includes(field.type)) {
                                cellValue = row[field.name] ? 'YYYY-MM-DD HH:mm' : ''; // Standard date format length
                            } else {
                                cellValue = String(row[field.name] || '');
                            }
                        }

                        maxContentLength = Math.max(maxContentLength, cellValue.length);
                    });

                    // BALANCED: Header visibility + prevent horizontal scroll
                    // Calculate conservative minimum width for header
                    const headerWidthEstimate = headerLength * 6; // Reduced from 8px to 6px per character
                    const minWidthForHeader = Math.max(80, headerWidthEstimate + 30); // Reduced padding from 40px to 30px

                    // More conservative flex calculations to fit within grid
                    const contentBasedFlex = maxContentLength / 20; // Increased divisor from 15 to 20
                    const headerBasedFlex = headerLength / 15; // Increased divisor from 10 to 15

                    // Balance header needs with content, but keep it moderate
                    const baseFlex = Math.max(headerBasedFlex, contentBasedFlex * 0.7); // Further reduced content weight

                    // More conservative field type adjustments
                    let flexMultiplier = 1;
                    switch (field.type) {
                        case 'datetime':
                        case 'date':
                            flexMultiplier = 1.05; // Further reduced
                            break;
                        case 'email':
                            flexMultiplier = 1.1; // Further reduced from 1.2
                            break;
                        case 'phone':
                            flexMultiplier = 1.0; // Neutral
                            break;
                        case 'select':
                            flexMultiplier = 1.05; // Further reduced
                            break;
                        case 'number':
                            flexMultiplier = 0.85; // Smaller for numbers
                            break;
                        default:
                            flexMultiplier = 1;
                    }

                    // More conservative flex range to prevent oversized columns
                    const finalFlex = Math.max(0.6, Math.min(2.5, baseFlex * flexMultiplier));

                    return {
                        flex: finalFlex,
                        minWidth: Math.min(minWidthForHeader, 150), // Cap minimum width at 150px
                        estimatedHeaderWidth: headerWidthEstimate
                    };
                };

                const newColumnDefs = sortedFields.map(field => {
                    const columnConfig = calculateColumnWidth(field, mergedRows);
                    return {
                        field: field.name,
                        headerName: t(field.label),
                        fieldType: field.type,
                        flex: columnConfig.flex,
                        minWidth: columnConfig.minWidth, // Calculated to fit header
                        maxWidth: 300 // Reduced max to prevent excessive width
                    };
                });

                setColumnDefs(newColumnDefs);

                // Update pagination state with server response
                if (fetchedData) {
                    // Use correct API structure - pagination data at root level
                    const serverTotalRecords = fetchedData.totalRecords || 0;
                    const serverCurrentPage = fetchedData.page || pageToFetch;
                    const serverTotalPages = fetchedData.totalPages || 0;
                    const recordsReceived = fetchedData?.data?.records?.length || 0;

                    setTotalRecords(serverTotalRecords);
                    // Only update current page if it's different from what we requested to avoid loops
                    if (serverCurrentPage !== pageToFetch) {
                        setCurrentPage(Math.max(1, serverCurrentPage));
                    }
                }
                

                setRows(mergedRows);
            } catch (error) {
                console.error('Failed to fetch module records:', error);
            } finally {
                // Ensure loading shows for at least 500ms for better UX in modal
                const elapsedTime = Date.now() - startTime;
                const minLoadingTime = 300;
                const remainingTime = Math.max(0, minLoadingTime - elapsedTime);
                
                setTimeout(() => {
                    setLoading(false);
                }, remainingTime);
            }
        };


        fetchRecordsAndColumns();

    }, [effectDeps]);



    const onRowClickedHandler = useCallback((event) => {
        if (mode !== 'link') {
            if (event?.data) navigate(`/crm/${moduleName}/records/${event.data._id}`)
        }
        else {
            onSelectionChanged([event.data])
        }
    }, [moduleName, mode])

    const newRecordModalToggel = useCallback(() => {
        if (onNewRowAdded && showNewRecordModal) {
            onNewRowAdded()
        }
        if (showNewRecordModal && newRecord) {
            return navigate(`/crm/${moduleName}/records/`)
        }
        if (!showNewRecordModal) {
            setNewRecordLoading(false); // Reset loading when opening
        }
        setShowNewRecordModal((oldState) => !oldState)
    }, [showNewRecordModal, onNewRowAdded, moduleName, newRecord])

    const filterRecordsModalToggel = useCallback(() => {
        setFilterRecordsModal((oldState) => !oldState)
    }, [showFilterRecordsModal])

    const onClearFiltersHandler = useCallback(() => {
        setLoading(true);
        // Only clear backend filters if not using local filters
        if (!useLocalFilters) {
            clearModuleFilters?.(selectedCrmModule?._id);
        }
        setFiltersArr([])
    }, [selectedCrmModule?._id, useLocalFilters])


    const linkRecordModalToggel = useCallback(() => {
        setLinkRecordModal((oldState) => !oldState)
    }, [linkRecordModal])

    const unLinkRecordModalToggel = useCallback(() => {
        setUnLinkRecordModal((oldState) => !oldState)
    }, [unLinkRecordModal])

    const onReloadGridHandler = useCallback(() => {
        setReloadGridTogggle((oldReloadState) => !oldReloadState)
    }, [])

    const onLinkOperationStart = useCallback(() => {
        setLinkLoading(true);
    }, [])

    const onLinkOperationComplete = useCallback(() => {
        // Trigger grid reload - the grid's natural loading state will take over
        onReloadGridHandler();

        // Reset linkLoading immediately - the grid's loading state will show spinner during reload
        setLinkLoading(false);
    }, [onReloadGridHandler])

    // Force refresh by resetting page to 1 - useful when data structure changes
    const forceRefresh = useCallback(() => {
        setCurrentPage(1)
        setReloadGridTogggle((oldReloadState) => !oldReloadState)
    }, [])
    
    const onPaginationChangedHandler = useCallback(({ page, limit }) => {
        // If this is the first dynamic page size update (limit changed from default 10 to calculated size)
        // and we're on page 1, this is likely the initial dynamic calculation
        if (page >= 1 && limit !== 10 && currentPageSize === 10 && !dynamicPageSizeUpdateRef.current) {
            dynamicPageSizeUpdateRef.current = true;
            setCurrentPageSize(limit);
            setWaitingForDynamicPageSize(false); // Stop waiting, we have the dynamic page size

            // Clear the timeout since we got the dynamic page size earlier
            if (initialFetchDelayRef.current) {
                clearTimeout(initialFetchDelayRef.current);
                initialFetchDelayRef.current = null;
            }

            // Reset the flag after a short delay to allow for normal pagination later
            setTimeout(() => {
                dynamicPageSizeUpdateRef.current = false;
            }, 1000);

            // Don't return - let it fetch data with the new page size
        }

        setLoading(true);
        setCurrentPage(page);
        setCurrentPageSize(limit);
    }, [currentPageSize]);

    const onSortChangedHandler = useCallback(({ field, order }) => {
        setLoading(true);
        setSortBy(field);
        setSortOrder(order);
        // Reset to first page when sorting changes
        setCurrentPage(1);
    }, []);

    const onNewRecordAddedHandler = useCallback((record) => {
        setNewRecordLoading(false); // Stop loading after record is added
        // For non-linking scenarios, trigger reload immediately
        // For linking scenarios, reload is handled in ModuleNewRecord after linking completes
        if (!relatedToRecordId || !relatedModuleObj) {
            forceRefresh() // Use force refresh to ensure new data is shown
        }
    }, [relatedToRecordId, relatedModuleObj, forceRefresh])

    if (!onSelectionChanged)
        onSelectionChanged = (rows) => {
            setSelectedRows(rows)
        }

    const onShowConfirmationModal = useCallback((titleKey, bodyKey, onConfirm, customDeleteBtnName) => {
        setConfirmationModal({
            show: true,
            titleKey,
            bodyKey,
            onConfirm,
            customDeleteBtnName
        })
    }, [])

    const onRemoveRowsClickedHandler = useCallback(async () => {
        if (selectedRows.length === 0) return;

        setDeleteLoading(true);

        // Show loading spinner in alert modal
        alertCtx.showAlert('', <div style={{textAlign: 'center', padding: '20px'}}><div className="spinner-border" role="status"><span className="sr-only">Loading...</span></div></div>, 'sm')

        try {
            // Extract all record IDs
            const recordIds = selectedRows.map(row => row._id);

            // Send batch delete request
            const response = await deleteBaseRecords(recordIds);

            // Refresh the grid after successful deletion
            setTimeout(() => {
                forceRefresh();
            }, 100);

            // Show success message
            const deletedCount = response?.data?.deletedCount || recordIds.length;
            alertCtx.showAlert(t('success'), t('Successfully deleted {{count}} record(s).', { count: deletedCount }), 'sm');

        } catch (error) {
            // Show error message
            console.error('Batch delete failed:', error);
            alertCtx.showAlert(t('fail'), t('Failed to delete records. You may not have permission or records may not exist.'), 'sm');
        } finally {
            setDeleteLoading(false);
        }
    }, [selectedRows, forceRefresh, alertCtx, t])

    const onDeleteRowsClickedWithPromptHandler = () => {
        onShowConfirmationModal(t('Records Deletion'), t('Are you sure you want to delete these records?'),
            () => {
                onRemoveRowsClickedHandler()
                setConfirmationModal(() => ({ show: false }))
            }, t('Delete'))

    }

    const onImportClickedHandler = useCallback(() => {
        // Show password confirmation modal
        setShowPasswordConfirmImport(true)
    }, [])

    const onImportConfirmedHandler = useCallback(() => {
        // After password verification, show import modal
        setImportRecordsModal(true)
    }, [])

    const onImportModalToggle = useCallback(() => {
        // Toggle import modal state
        setImportRecordsModal((oldState) => !oldState)
    }, [])

    const onExportClickedHandler = useCallback(() => {
        // Show password confirmation modal
        setShowPasswordConfirmExport(true)
    }, [])

    const onExportConfirmedHandler = useCallback(async () => {
        // After password verification, perform export
        setLoading(true);
        alertCtx.showAlert('', <div style={{textAlign: 'center', padding: '20px'}}><div className="spinner-border" role="status"><span className="sr-only">Loading...</span></div></div>, 'sm')

        try {
            // Get ALL fields from the module (not just showColumns)
            const allFields = selectedCrmModule.sections
                .filter(section => section.name !== 'systemSection')
                .flatMap(section => section.fields || []);

            // Sort fields by order
            const sortedFields = allFields.sort((a, b) => (a.order || 0) - (b.order || 0));

            // Prepare filters
            const filters = [...filtersArr]
            if (relatedToRecordId && mode === 'unlink') {
                filters.push({
                    field: "relatedRecords.records",
                    operator: "equals",
                    value: relatedToRecordId
                })
            }

            // Fetch ALL records (no pagination - use a very large limit)
            const fetchedData = await getBaseRecordsByModuleId(
                selectedCrmModule._id,
                1,
                999999, // Large number to get all records
                sortBy,
                sortOrder,
                filters
            );

            const fetchedRows = fetchedData?.data?.records || [];
            const mergedRows = getMergedStaticDynamicRowFields(fetchedRows);

            // Generate CSV
            const csvHeaders = sortedFields.map(field => t(field.label));
            const csvRows = mergedRows.map(row => {
                return sortedFields.map(field => {
                    let value = row[field.name] || '';

                    // Handle different field types for CSV export
                    if (typeof value === 'object' && value !== null) {
                        // For objects with crmName (like related records), use crmName
                        if (value.crmName) {
                            value = value.crmName;
                        }
                        // For file objects, use fileName
                        else if (value.fileName) {
                            value = value.fileName;
                        }
                        // For other objects (like links), convert to string
                        else {
                            value = value.value || value.name || JSON.stringify(value);
                        }
                    }
                    // Format datetime and date fields to Israel timezone
                    else if (['datetime', 'date'].includes(field.type) && value) {
                        value = dayjs.utc(value).tz('Asia/Jerusalem').format('DD/MM/YYYY HH:mm');
                    }

                    // Escape quotes and wrap in quotes if contains comma or quote
                    value = String(value).replace(/"/g, '""');
                    if (value.includes(',') || value.includes('"') || value.includes('\n')) {
                        value = `"${value}"`;
                    }

                    return value;
                });
            });

            // Combine headers and rows
            const csvContent = [
                csvHeaders.join(','),
                ...csvRows.map(row => row.join(','))
            ].join('\n');

            // Create and download CSV file
            const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            link.setAttribute('download', `${selectedCrmModule.label}_export_${new Date().toISOString().split('T')[0]}.csv`);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            alertCtx.showAlert(t('success'), t('Export completed successfully. {{count}} records exported.', { count: mergedRows.length }), 'sm');
        } catch (error) {
            console.error('Export failed:', error);
            alertCtx.showAlert(t('fail'), t('Failed to export records.'), 'sm');
        } finally {
            setLoading(false);
        }
    }, [selectedCrmModule, filtersArr, relatedToRecordId, mode, sortBy, sortOrder, getMergedStaticDynamicRowFields, alertCtx, t])

    // Helper function to find email/phone fields from module sections and static fields
    const getFieldsFromModule = useCallback(() => {
        const emailFields = []
        const phoneFields = []

        // Priority 1: Static fields (email and mainPhone)
        const hasStaticEmail = true // Static email field exists
        const hasStaticMainPhone = true // Static mainPhone field exists

        // Priority 2: Dynamic fields from sections
        if (selectedCrmModule?.sections) {
            selectedCrmModule.sections.forEach(section => {
                if (section.name !== 'systemSection' && section.fields) {
                    section.fields.forEach(field => {
                        if (field.type === 'email' && field.name !== 'email') {
                            emailFields.push(field.name)
                        }
                        if (field.type === 'phone' && field.name !== 'mainPhone') {
                            phoneFields.push(field.name)
                        }
                    })
                }
            })
        }

        return {
            emailFields: hasStaticEmail ? ['email', ...emailFields] : emailFields,
            phoneFields: hasStaticMainPhone ? ['mainPhone', ...phoneFields] : phoneFields
        }
    }, [selectedCrmModule])

    // Check if selected rows have emails
    const selectedRowsHaveEmails = useMemo(() => {
        if (selectedRows.length === 0) return false
        const { emailFields } = getFieldsFromModule()
        if (emailFields.length === 0) return false

        return selectedRows.some(row => {
            // Check static email field first (priority)
            if (row.email) return true
            // Check dynamic email fields
            if (row.fields) {
                for (const fieldName of emailFields) {
                    const emailField = row.fields.find(f => f.name === fieldName)
                    if (emailField?.value) return true
                }
            }
            // Check merged field values (already flattened)
            for (const fieldName of emailFields) {
                if (row[fieldName]) return true
            }
            return false
        })
    }, [selectedRows, getFieldsFromModule])

    // Check if selected rows have phones
    const selectedRowsHavePhones = useMemo(() => {
        if (selectedRows.length === 0) return false
        const { phoneFields } = getFieldsFromModule()
        if (phoneFields.length === 0) return false

        return selectedRows.some(row => {
            // Check static mainPhone field first (priority)
            if (row.mainPhone) return true
            // Check dynamic phone fields
            if (row.fields) {
                for (const fieldName of phoneFields) {
                    const phoneField = row.fields.find(f => f.name === fieldName)
                    if (phoneField?.value) return true
                }
            }
            // Check merged field values (already flattened)
            for (const fieldName of phoneFields) {
                if (row[fieldName]) return true
            }
            return false
        })
    }, [selectedRows, getFieldsFromModule])

    // Handler for sending email to selected rows
    const onSendEmailClickedHandler = useCallback(() => {
        if (selectedRows.length === 0) return

        const { emailFields } = getFieldsFromModule()

        if (emailFields.length === 0) {
            return alertCtx.showAlert(t('fail'), t('No email fields found in this module.'), 'sm')
        }

        // Collect all email addresses from selected rows
        const emails = []

        selectedRows.forEach(row => {
            // Check static email field first (priority)
            if (row.email) {
                emails.push(row.email)
            }
            // Check dynamic email fields
            else if (row.fields) {
                for (const fieldName of emailFields) {
                    const emailField = row.fields.find(f => f.name === fieldName)
                    if (emailField?.value) {
                        emails.push(emailField.value)
                        break // Take only the first email found per row
                    }
                }
            }
            // Check merged field values (already flattened)
            else {
                for (const fieldName of emailFields) {
                    if (row[fieldName]) {
                        emails.push(row[fieldName])
                        break // Take only the first email found per row
                    }
                }
            }
        })

        // Filter unique and valid emails
        const uniqueEmails = [...new Set(emails.filter(email => email))]

        if (uniqueEmails.length === 0) {
            return alertCtx.showAlert(t('fail'), t('No email addresses found for selected records.'), 'sm')
        }

        // First email goes to 'to', rest go to 'cc'
        const mailData = {
            to: uniqueEmails[0],
            cc: uniqueEmails.length > 1 ? uniqueEmails.slice(1).join(',') : ''
        }

        onSelectConv?.(mailData, 'mail')
        navigate('/')
    }, [selectedRows, getFieldsFromModule, alertCtx, t, onSelectConv, navigate])

    // Handler for adding phone numbers to dial list
    const onAddToDialListClickedHandler = useCallback(() => {
        if (selectedRows.length === 0) return

        const { phoneFields } = getFieldsFromModule()

        if (phoneFields.length === 0) {
            return alertCtx.showAlert(t('fail'), t('No phone fields found in this module.'), 'sm')
        }

        // Collect all phone numbers from selected rows
        const phones = []

        selectedRows.forEach(row => {
            // Check static mainPhone field first (priority)
            if (row.mainPhone) {
                phones.push(row.mainPhone)
            }
            // Check dynamic phone fields
            else if (row.fields) {
                for (const fieldName of phoneFields) {
                    const phoneField = row.fields.find(f => f.name === fieldName)
                    if (phoneField?.value) {
                        phones.push(phoneField.value)
                        break // Take only the first phone found per row
                    }
                }
            }
            // Check merged field values (already flattened)
            else {
                for (const fieldName of phoneFields) {
                    if (row[fieldName]) {
                        phones.push(row[fieldName])
                        break // Take only the first phone found per row
                    }
                }
            }
        })

        // Filter unique and valid phones
        const uniquePhones = [...new Set(phones.filter(phone => phone))]

        if (uniquePhones.length === 0) {
            return alertCtx.showAlert(t('fail'), t('No phone numbers found for selected records.'), 'sm')
        }

        // Add all phones to dial list
        uniquePhones.forEach(phone => {
            OnAddToDialListClicked?.(phone)
        })

        alertCtx.showAlert(t('success'), t('Added {{count}} phone number(s) to dial list.', { count: uniquePhones.length }), 'sm')
    }, [selectedRows, getFieldsFromModule, alertCtx, t, OnAddToDialListClicked])


    return (
        <>
            <section>
                <CrmGrid
                    rowData={rows}
                    columnDefs={columnDefs}
                    multiSelectionEnable={(multiSelectionEnable || !['CRMUser','Rules'].find(moduleType => selectedCrmModule?.name === moduleType)) ?? true}
                    supportManageColumns={false}
                    supportMore={false}
                    onRowClicked={((!suppressOpenRecord || mode === 'link') && onRowClickedHandler) ? onRowClickedHandler : () => { }}
                    onNewClicked={
                        !suppressOpenNewRecord &&
                        !['CRMUser'].find(moduleType => selectedCrmModule?.name === moduleType) &&
                        isInsertPermissions &&
                        newRecordModalToggel
                    }
                    onFilterClicked={filterRecordsModalToggel}
                    onLinkClicked={isUpdatePermissions && !suppressLinkModules && relatedToRecordId && linkRecordModalToggel}
                    onUnlinkClicked={isUpdatePermissions && !suppressLinkModules && relatedToRecordId && unLinkRecordModalToggel}
                    pagination={true}
                    className={`${styles['records-grid']} ${gridCssClass}`}
                    isGridMinimized={isGridMinimized}
                    onMinimizeToggleClicked={onMinimizeToggleClicked}
                    gridTitle={gridTitle}
                    isSubGrid={isSubGrid}
                    suppressOpenRecord={suppressOpenRecord && mode !== 'link'}
                    onSelectionChanged={onSelectionChanged}
                    isFiltersSelected={Boolean(filtersArr?.length)}
                    onClearFilters={onClearFiltersHandler}
                    onRemoveRowsClicked={
                        !moduleObj &&
                        Boolean(selectedRows?.length) &&
                        !['CRMUser'].find(moduleType => selectedCrmModule?.name === moduleType) &&
                        isDeletePermissions &&
                        onDeleteRowsClickedWithPromptHandler
                    }
                    onSelectConv={onSelectConv}
                    OnAddToDialListClicked={OnAddToDialListClicked}
                    onImportClicked={
                        !['CRMUser'].find(moduleType => selectedCrmModule?.name === moduleType) &&
                        !moduleObj &&
                        loggedUser?.permissionType <= 3 &&
                        onImportClickedHandler
                    }
                    onExportClicked={
                        !['CRMUser'].find(moduleType => selectedCrmModule?.name === moduleType) &&
                        !moduleObj &&
                        loggedUser?.permissionType <= 3 &&
                        onExportClickedHandler
                    }
                    onSendEmailClicked={
                        showBulkEmailButton &&
                        selectedRowsHaveEmails &&
                        !['CRMUser', 'Rules'].find(moduleType => selectedCrmModule?.name === moduleType) &&
                        onSelectConv &&
                        onSendEmailClickedHandler
                    }
                    onAddToDialListClicked={
                        showBulkDialListButton &&
                        selectedRowsHavePhones &&
                        !['CRMUser', 'Rules'].find(moduleType => selectedCrmModule?.name === moduleType) &&
                        OnAddToDialListClicked &&
                        onAddToDialListClickedHandler
                    }
                    // Server-side pagination props
                    totalRecords={totalRecords}
                    onPaginationChanged={onPaginationChangedHandler}
                    // Server-side sorting props
                    onSortChanged={onSortChangedHandler}
                    // Loading states
                    loading={loading || deleteLoading || linkLoading || newRecordLoading}
                    // Lead highlighting props
                    selectedCrmModule={selectedCrmModule}
                />
            </section>
            {showNewRecordModal &&
                <SubModal
                    modalTitle={t('Create new X Recrod module', { module: selectedCrmModule?.label })}
                    onHide={newRecordModalToggel}
                >
                    <ModuleNewRecord 
                        onCancelClicked={newRecordModalToggel} 
                        onNewRecordAdded={onNewRecordAddedHandler} 
                        onReloadGrid={forceRefresh} 
                        onSaveStarted={() => setNewRecordLoading(true)}
                        moduleObj={moduleObj} 
                        relatedToRecordId={relatedToRecordId} 
                        relatedModuleObj={relatedModuleObj} 
                    />
                </SubModal>
            }
            {showFilterRecordsModal &&
                <SubModal
                    modalTitle={t('Filter records in module X', { module: selectedCrmModule?.label })}
                    onHide={filterRecordsModalToggel}
                >
                    <ModuleFilterRecords moduleObj={moduleObj} onCancelClicked={filterRecordsModalToggel} useLocalFilters={useLocalFilters} onApplyFilters={(filters, shouldShowLoading = true) => {
                        // Only set loading if explicitly requested (not for initial saved filter loading)
                        if (shouldShowLoading) {
                            setLoading(true);
                        }
                        setFiltersArr(filters);
                    }} />
                </SubModal>
            }
            {
                linkRecordModal && !suppressLinkModules && moduleObj &&
                <SubModal
                    modalTitle={t('Search in module X', { module: moduleObj.label })}
                    onHide={linkRecordModalToggel}
                    landscapeMode={true}
                >
                    <ModuleRecordRelation onCancelClicked={linkRecordModalToggel} mode={'multilink'} onReloadGrid={onLinkOperationComplete} onOperationStart={onLinkOperationStart} moduleObj={moduleObj} relatedToRecordId={relatedToRecordId} onSelectConv={onSelectConv} OnAddToDialListClicked={OnAddToDialListClicked} />
                </SubModal>
            }
            {
                unLinkRecordModal && !suppressLinkModules && relatedToRecordId && moduleObj &&
                <SubModal
                    modalTitle={t('Search in module X', { module: moduleObj.label })}
                    onHide={unLinkRecordModalToggel}
                    landscapeMode={true}
                >
                    <ModuleRecordRelation onCancelClicked={unLinkRecordModalToggel} mode={'unlink'} onReloadGrid={onLinkOperationComplete} onOperationStart={onLinkOperationStart} moduleObj={moduleObj} relatedToRecordId={relatedToRecordId} onSelectConv={onSelectConv} OnAddToDialListClicked={OnAddToDialListClicked} />
                </SubModal>
            }
            {
                importRecordsModal &&
                <SubModal
                    modalTitle={t('Import in module X', { module: selectedCrmModule?.label })}
                    onHide={onImportModalToggle}
                    landscapeMode={false}
                >
                    <ModuleImportRecords
                        onCancelClicked={onImportModalToggle}
                        onReloadGrid={onReloadGridHandler}
                    />
                </SubModal>
            }
            {
                <ConfirmationModal
                    {...confirmationModal}
                    onHide={() => setConfirmationModal(() => ({ show: false }))}
                />
            }
            <PasswordConfirmationModal
                show={showPasswordConfirmImport}
                onHide={() => setShowPasswordConfirmImport(false)}
                onConfirm={onImportConfirmedHandler}
                titleKey="Confirm Import"
                messageKey="Please enter your password to confirm the import operation."
                confirmButtonText={t('Confirm Import')}
            />
            <PasswordConfirmationModal
                show={showPasswordConfirmExport}
                onHide={() => setShowPasswordConfirmExport(false)}
                onConfirm={onExportConfirmedHandler}
                titleKey="Confirm Export"
                messageKey="Please enter your password to confirm the export operation."
                confirmButtonText={t('Confirm Export')}
            />
        </>
    )
}
export default ModuleRecords